import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/api/client';
import type { User, Company, UserRole, BillingInfo } from '@/types';
import { setAppLocale, type SupportedLocale } from '@/i18n';

interface LoginResponse {
  user: User;
  companies: Company[];
}

export const useAuthStore = defineStore('auth', () => {
  // State - tokens are now managed via httpOnly cookies, not stored in frontend
  const user = ref<User | null>(null);
  const companies = ref<Company[]>([]);
  const currentCompanyId = ref<string | null>(null);
  const isAuthChecked = ref(false);
  const billingInfo = ref<BillingInfo | null>(null);

  // Computed
  const isAuthenticated = computed(() => !!user.value);
  const isFreeTier = computed(() => !billingInfo.value || billingInfo.value.status === 'free');
  const isSubscriptionActive = computed(() => billingInfo.value?.status === 'active');

  const currentCompany = computed(() =>
    companies.value.find(c => c.id === currentCompanyId.value)
  );

  const userRole = computed<UserRole | null>(() =>
    currentCompany.value?.role || null
  );

  const isAdmin = computed(() => userRole.value === 'admin');
  const isManager = computed(() => userRole.value === 'manager' || isAdmin.value);

  // Actions
  async function login(email: string, password: string) {
    const response = await api.post<{ data: LoginResponse }>('/auth/login', {
      email,
      password,
    });

    const data = response.data.data;

    user.value = data.user;
    companies.value = data.companies;

    // Sync UI locale with user's language preference
    setAppLocale((data.user.language || 'en') as SupportedLocale);

    // Set first company as default
    if (data.companies.length > 0) {
      currentCompanyId.value = data.companies[0].id;
    }

    // Persist user data to localStorage (not tokens)
    saveToStorage();

    // Load billing info for current company
    loadBillingInfo();

    return data;
  }

  async function register(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    invitationToken?: string;
    language?: string;
  }) {
    const response = await api.post<{ data: User }>('/auth/register', data);
    return response.data.data;
  }

  async function refreshTokens() {
    // Just call the refresh endpoint - cookies are handled automatically
    await api.post('/auth/refresh', {});
  }

  async function logout() {
    // Clear state immediately so isAuthenticated becomes false
    // This prevents components from making API calls during logout
    user.value = null;
    companies.value = [];
    currentCompanyId.value = null;
    clearStorage();
    setAppLocale('en');

    // Then call API to clear server-side cookies
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors during logout
    }
  }

  function setCurrentCompany(companyId: string) {
    const company = companies.value.find(c => c.id === companyId);
    if (company) {
      currentCompanyId.value = companyId;
      saveToStorage();
      loadBillingInfo();
    }
  }

  async function loadBillingInfo() {
    if (!currentCompanyId.value) {
      billingInfo.value = null;
      return;
    }
    try {
      const response = await api.get<{ data: BillingInfo }>('/billing/status');
      billingInfo.value = response.data.data;
    } catch {
      billingInfo.value = null;
    }
  }

  function saveToStorage() {
    // Only save user data, not tokens (tokens are in httpOnly cookies)
    localStorage.setItem('aimm_auth', JSON.stringify({
      user: user.value,
      companies: companies.value,
      currentCompanyId: currentCompanyId.value,
    }));
  }

  function loadFromStorage() {
    const stored = localStorage.getItem('aimm_auth');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        user.value = data.user;
        companies.value = data.companies || [];
        currentCompanyId.value = data.currentCompanyId;

        // Restore locale from stored user preference
        if (data.user?.language) {
          setAppLocale(data.user.language as SupportedLocale);
        }

        // Ensure currentCompanyId is valid
        if (companies.value.length > 0) {
          const hasValidCompany = companies.value.some(c => c.id === currentCompanyId.value);
          if (!hasValidCompany) {
            // Reset to first company if current is invalid
            currentCompanyId.value = companies.value[0].id;
            saveToStorage();
          }
        }
      } catch {
        clearStorage();
      }
    }
    isAuthChecked.value = true;
  }

  function clearStorage() {
    localStorage.removeItem('aimm_auth');
  }

  // Check if session is still valid by making a request
  async function checkAuth() {
    if (!user.value) {
      isAuthChecked.value = true;
      return false;
    }

    try {
      // Try to get profile - if cookies are valid, this will succeed
      const response = await api.get<{ data: User }>('/auth/me');
      user.value = response.data.data;
      setAppLocale((response.data.data.language || 'en') as SupportedLocale);
      isAuthChecked.value = true;
      return true;
    } catch {
      // Session invalid, clear local state
      user.value = null;
      companies.value = [];
      currentCompanyId.value = null;
      clearStorage();
      isAuthChecked.value = true;
      return false;
    }
  }

  // Initialize from storage
  loadFromStorage();

  return {
    // State
    user,
    companies,
    currentCompanyId,
    isAuthChecked,
    billingInfo,

    // Computed
    isAuthenticated,
    currentCompany,
    userRole,
    isAdmin,
    isManager,
    isFreeTier,
    isSubscriptionActive,

    // Actions
    login,
    register,
    refreshTokens,
    logout,
    setCurrentCompany,
    loadFromStorage,
    saveToStorage,
    checkAuth,
    loadBillingInfo,
  };
});
